import { Grid, Hidden } from '@mui/material';
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { NavLink, useRouteMatch } from 'react-router-dom';
import { GuidedProcessSupportBlock } from '../../global/components/guided-process-support-block';
import { InfoBarWrapper } from '../../global/components/layout/info-bar-wrapper';
import { MainContent } from '../../global/components/layout/main-content-container';
import { PageContainer } from '../../global/components/layout/page-container';
import { SideBarContent } from '../../global/components/layout/sidebar-content';
import { NameClientNumber } from '../../global/components/name-client-number';
import { useQuery } from '../../global/hooks/useQuery';
import { DemographicAppKeys } from '../../global/models/demographics-model';
import { LabelSettingProps } from '../../global/models/epi-model';
import { TcsmEpiApiState, TcsmStates } from '../../root/tcsm-model';
import { PersonalInfoState } from '../account-info/features/personal-info/personal-info-models';
import { MyCpgPodsSettings, MyCpgPodsTypes } from './mycpg-pod-models';
import { DisabilityPlanDetail } from './pods-components/disability-plan-detail';
import { HealthPlanDetail } from './pods-components/health-plan-detail';
import { AnnuityPlanDetail } from './pods-components/annuity-plan-detail';
import { LifePlanDetail } from './pods-components/life-insurance-plan-detail';
import { PensionPlanDetail } from './pods-components/pension-plan-detail';
import { PodNavigation } from './pods-components/pod-navigation';
import { RsvpPlanDetail } from './pods-components/rsvp-plan-detail';
import { DcPlanDetail } from './pods-components/pension-dc-plan-detail';
import { GuidedProcessSupportProps } from '../arc/models/arc-epi-props';
import { standAlone } from '../../global/ducks/epi-api';
import { EmploymentHistory } from '../account-info/features/employee-history/employment-history';
import { TcsmAppConfigs } from '../../global/models/configuration-models';


interface Props {
	settings: MyCpgPodsSettings;
	appConfig: TcsmAppConfigs;
}

export function MyCpgPodsDetailPage(props: Props) {

	const { settings, appConfig } = props;
	const type = useQuery()?.get('type') as MyCpgPodsTypes;
	const gpsSettings = useSelector<TcsmStates, GuidedProcessSupportProps>(state => state.epiProps?.gpsSettingsState?.epiContent);
	const personState = useSelector<TcsmStates, PersonalInfoState>(state => state.personalInfo[DemographicAppKeys.myCPG]);
	const labelSettings = useSelector<TcsmStates, LabelSettingProps>(a => a.epiProps?.labelSettingState?.epiContent);
	const epiProps = useSelector<TcsmStates, TcsmEpiApiState>(state => state.epiProps);
	const routeMatch = useRouteMatch();

	useEffect(() => {
		window.scrollTo(0, 0);
	}, []);

	const getGps = () => {

		let contents = null;
		let title = null;

		switch (type) {
			//render detail view based on type
			case MyCpgPodsTypes.PENSION:
				contents = gpsSettings?.pensionGps;
				title = gpsSettings?.pensionGpsTitle;
				break;
			case MyCpgPodsTypes.DC:
				contents = gpsSettings?.dcGps;
				title = gpsSettings?.dcGpsTitle;
				break;
			case MyCpgPodsTypes.ANNUITY:
				contents = gpsSettings?.annuityGps;
				title = gpsSettings?.annuityGpsTitle;
				break;
			case MyCpgPodsTypes.DISABLE:
				contents = gpsSettings?.disabilityGps;
				title = gpsSettings?.disabilityGpsTitle;
				break;
			case MyCpgPodsTypes.HEALTH:
				contents = gpsSettings?.healthGps;
				title = gpsSettings?.healthGpsTitle;
				break;
			case MyCpgPodsTypes.LIFE:
				contents = gpsSettings?.lifeGps;
				title = gpsSettings?.lifeGpsTitle;
				break;
			case MyCpgPodsTypes.RSVP:
				contents = gpsSettings?.rsvpGps;
				title = gpsSettings?.rsvpGpsTitle;
				break;
			case MyCpgPodsTypes.EMPLOYMENT:
				title = gpsSettings?.employmentHistoryTitle;
				contents = gpsSettings?.employmentHistoryGps;
				break;
			default:
				break;

		}
		return { title, contents };
	};

	const getBreadCrumbsText = () => {
		const matchRoute = epiProps?.siteSettingsState?.siteSettings?.routeBreadcrumbs?.filter(a => a.route === `${window?.location?.pathname}${window?.location?.search}`);
		return matchRoute?.length === 1 ? matchRoute[0].breadcrumb : '';
	};

	const renderDetaiView = () => {
		switch (type) {
			//render detail view based on type
			case MyCpgPodsTypes.PENSION:
				return <PensionPlanDetail settings={settings} />;
			case MyCpgPodsTypes.DC:
				return <DcPlanDetail settings={settings} />;
			case MyCpgPodsTypes.ANNUITY:
				return <AnnuityPlanDetail settings={settings} />;
			case MyCpgPodsTypes.DISABLE:
				return <DisabilityPlanDetail settings={settings} />;
			case MyCpgPodsTypes.HEALTH:
				return <HealthPlanDetail settings={settings} />;
			case MyCpgPodsTypes.LIFE:
				return <LifePlanDetail settings={settings} />;
			case MyCpgPodsTypes.RSVP:
				return <RsvpPlanDetail settings={settings} />;
			case MyCpgPodsTypes.EMPLOYMENT:
				return <EmploymentHistory appConfig={appConfig} />;
			default:
				return null;

		}
	};

	const renderNav = () => {
		return (
			<InfoBarWrapper breadcrumbWrapper>
				{
					standAlone ?
						<NavLink to={routeMatch?.path}>Back</NavLink>
						:
						<>
							<span>
								<NavLink to={routeMatch?.path}>MyCPG</NavLink><span className='breadcrumbs-separator'>/</span>
							</span>
							<span>{getBreadCrumbsText()}</span>
						</>
				}
			</InfoBarWrapper>
		);
	};

	const gpsItem = getGps();

	return (
		<>
			{renderNav()}
			<InfoBarWrapper>
				<NameClientNumber client={personState?.currentPerson} clientNumberLabel={labelSettings?.clientNumberLabel} />
				<Grid item md={4} className='infobar-nav'>
					<PodNavigation />
				</Grid>
			</InfoBarWrapper>
			<PageContainer>
				<MainContent>
					{renderDetaiView()}
				</MainContent>
				<Hidden smDown>
					<SideBarContent>
						<GuidedProcessSupportBlock title={gpsItem?.title} contents={gpsItem?.contents} />
					</SideBarContent>
				</Hidden>
			</PageContainer>
		</>

	);

}

